import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { listNotifications, type NotificationRecord } from '@/features/planning/api';
import type { Language } from '@/types';

interface Props {
  language: Language;
  onOpenAll?: () => void;
  max?: number;
}

type Severity = NotificationRecord['severity'];

const SEV_DOT: Record<Severity, string> = {
  info: '#607C6E',
  warning: '#B97E1F',
  error: '#C24138',
};

export function NotificationsWidget({ language, onOpenAll, max = 4 }: Props) {
  const { session } = useAuth();
  const token = session?.access_token ?? null;
  const [items, setItems] = useState<NotificationRecord[]>([]);
  const [counts, setCounts] = useState<{ unseen: number; warning: number; error: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await listNotifications(token, false);
        if (cancelled) return;
        setItems(res.notifications.slice(0, max));
        setCounts(res.counts);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token, max]);

  const title = language === 'sv' ? 'NOTIFIERINGAR' : 'NOTIFICATIONS';
  const viewAll = language === 'sv' ? 'Se alla →' : 'View all →';
  const empty = language === 'sv' ? 'INGA AKTIVA VARNINGAR' : 'NO ACTIVE WARNINGS';

  return (
    <div className="cmd-panel cmd-panel-inner rounded p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div className="flex items-center gap-2">
          <span className="cmd-label">{title}</span>
          {counts && counts.unseen > 0 && (
            <span className="cmd-label text-[#8A6A47] tabular-nums">{counts.unseen}</span>
          )}
        </div>
        {onOpenAll && (
          <button
            type="button"
            onClick={onOpenAll}
            className="cmd-label text-[#8A6A47] hover:text-[#6E5438] transition-colors text-[9px]"
          >
            {viewAll}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-[#607C6E]" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex items-center gap-2 py-2">
          <div
            className="w-1.5 h-1.5 rounded-full shrink-0 cmd-pulse"
            style={{ background: '#1B7F4D' }}
          />
          <span className="text-xs font-mono text-[#1B7F4D]">{empty}</span>
        </div>
      ) : (
        <ul className="space-y-0.5">
          {items.map((n) => (
            <li
              key={n.id}
              className="flex items-start gap-2.5 px-1 py-2 border-b border-[#073331]/[0.06] last:border-0"
            >
              <div
                className="w-1.5 h-1.5 rounded-full shrink-0 mt-[5px]"
                style={{ background: SEV_DOT[n.severity] }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono text-[#073331] truncate leading-tight">{n.title}</div>
                {n.body && (
                  <div className="text-xs font-mono text-[#607C6E] truncate mt-0.5">{n.body}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
